//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'
import * as moment from 'moment'
import { formatNumber as globalizeFormatNumber, parseNumber as globalizeParseNumber } from 'globalize'

//#endregion

import { Enums, FileExtension } from '../models/common/enums'
import { IOpData } from '../models/util/interfaces'
import { KnownColor } from '../models/common/known-colors'
import { OpFile } from '../models/core'
import { OpData } from '../models/core/op-data'
import { OpDataType } from '../models/core/op-data-type'
import { SESSION } from '../models/custom/session'

import { DataProvider } from './data-provider'
import { LogHelper } from './log-helper'
import { Md5 } from './md5-helper'

export class ConvertHelper {
	// #region Consts

	/**
	 * Equal to yyyy-MM-dd HH:mm:ss.fff (C#) but for moment.js.
	 */
	public static get DefaultDateTimeFormat(): string {
		return 'YYYY-MM-DD HH:mm:ss.SSS'
	}

	private static get SNAKE_CASE_REGEXP(): RegExp {
		return /[A-Z]/g
	}
	private static get EXTRACT_RGBA_REGEXP(): RegExp {
		return /rgba\((.+)\)/g
	}
	private static get LOG_STRING(): string {
		return '[SIMAX.Helpers.ConvertHelper] - %s'
	}

	// #endregion

	// #region Colors

	public static hexToRGBA(hex: string): string {
		if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
			let colorParts: string[] = hex.substring(1).split('')

			if (colorParts.length == 3) {
				colorParts = [colorParts[0], colorParts[0], colorParts[1], colorParts[1], colorParts[2], colorParts[2]]
			}

			const colorHexValue: number = Number('0x' + colorParts.join(''))

			return 'rgba(' + [(colorHexValue >> 16) & 255, (colorHexValue >> 8) & 255, colorHexValue & 255].join(',') + ',1)'
		}

		if (/^#([A-Fa-f0-9]{4}){1,2}$/.test(hex)) {
			let colorParts: string[] = hex.substring(1).split('')

			if (colorParts.length == 4) {
				colorParts = [colorParts[0], colorParts[0], colorParts[1], colorParts[1], colorParts[2], colorParts[2], colorParts[3], colorParts[3]]
			}

			const colorHexValue: number = Number('0x' + colorParts.join(''))

			return 'rgba(' + [(colorHexValue >> 16) & 255, (colorHexValue >> 8) & 255, colorHexValue & 255, Math.floor((((colorHexValue >> 24) & 255) / 255) * 100) / 100].join(',') + ')'
		}

		throw new Error('Bad Hex')
	}

	static rgbToHex(rgb: string | number[]): string {
		let parts: number[] = null

		if (_.isString(rgb)) {
			const match: RegExpExecArray = ConvertHelper.EXTRACT_RGBA_REGEXP.exec(rgb)

			if (match != null) {
				const rgbValues: string = match[1]

				if (rgbValues != null) {
					parts = rgbValues
						.split(', ')
						.slice(0, 3)
						.map((part: string) => {
							return parseInt(part)
						})
				}
			}
		}

		if (_.isArray(rgb) && _.size(rgb) === 3) {
			parts = rgb
		}

		return parts
			.map((part: number) => {
				const hex = part.toString(16)

				return hex.length == 1 ? '0' + hex : hex
			})
			.join('')
	}

	public static knownColorEnumToHexColor(color: Enums.KnownColorsEnum): string {
		if (color != null) {
			const colorString = KnownColor.getColor(color)

			if (colorString != null) {
				const rgbValues: string[] = colorString.split(',')

				return _.chain(rgbValues)
					.slice(0, 3)
					.map(Number)
					.map((rgbValue: number) => {
						let hexValue: string = rgbValue.toString(16)

						if (_.size(hexValue) === 1) {
							hexValue = `0${hexValue}`
						}

						return hexValue
					})
					.join('')
					.value()
			}
		}

		return null
	}

	public static knownColorEnumToRGBAColor(color: Enums.KnownColorsEnum): string {
		if (color != null) {
			return 'rgba(' + KnownColor.getColor(color) + ')'
		}

		return null
	}

	// #endregion

	// #region DataTypes

	public static prepareDataValue(value: any, dataType: OpDataType, format?: string): any {
		if (value == null) {
			return null
		}

		if (dataType == null) {
			return value
		}

		let retVal: any = value

		switch (dataType.idDataTypeClass) {
			case Enums.DataTypeClass.Decimal:
			case Enums.DataTypeClass.Real:
			case Enums.DataTypeClass.Integer: {
				if (_.isString(value)) {
					if (value === '') {
						retVal = null
					} else {
						const number = Number(value)

						if (_.isFinite(number)) {
							retVal = number
						}
					}
				}

				break
			}
			case Enums.DataTypeClass.Datetime:
			case Enums.DataTypeClass.Date:
			case Enums.DataTypeClass.Time: {
				if (_.isString(value)) {
					value = ConvertHelper.stringToDate(value, format)
				}

				retVal = ConvertHelper.dateToString(value)

				break
			}
			case Enums.DataTypeClass.Text:
			case Enums.DataTypeClass.Boolean:
			case Enums.DataTypeClass.Binary: {
				retVal = value
				break
			}
			case Enums.DataTypeClass.Object:
			default: {
				throw new Error(`Unsupported data type class: ${dataType.idDataTypeClass} for datatype ${dataType.idDataType}`)
			}
		}

		return retVal
	}

	// #endregion

	// #region Date and Time

	public static dateRangeToTypeDateTimeCode(dateFrom: Date, dateTo: Date): any {
		// TODO Dodac klase TypeDateTimeCode
		const time: any = {} // TODO dodac TypeDateTimeCode i przeniesc to do ConvertHelpera

		if (dateFrom == null && dateTo != null) {
			time.DateTime1 = ConvertHelper.dateToString(dateTo, { netFormat: true })
			time.Mode = Enums.TypeDateTimeCodeModeType.LesserEqual
		} else if (dateFrom != null && dateTo == null) {
			time.DateTime1 = ConvertHelper.dateToString(dateFrom, { netFormat: true })
			time.Mode = Enums.TypeDateTimeCodeModeType.GreaterEqual
		} else if (dateFrom != null && dateTo != null) {
			time.Mode = Enums.TypeDateTimeCodeModeType.Between
			time.DateTime1 = ConvertHelper.dateToString(dateFrom, { netFormat: true })
			time.DateTime2 = ConvertHelper.dateToString(dateTo, { netFormat: true })
		} else {
			return null
		}

		return time
	}

	/**
	 * Formats provided date into DefaultDateTimeFormat or ASP.NET formats.
	 * @param date
	 * @see DefaultDateTimeFormat
	 */
	public static dateToString(date: Date, { netFormat = false, includeTimeZone = false } = {}): string {
		if (date == null) {
			throw new Error('The date cannot be undefined.')
		}

		if (netFormat) {
			return this.dateToMSDate(date, includeTimeZone)
		}

		return moment(date).format(ConvertHelper.DefaultDateTimeFormat)
	}

	private static dateToMSDate(date: Date, includeTimeZone: boolean = false): string {
		if (includeTimeZone === true) {
			const offsetString: string = this.getOffsetString(date)

			return '/Date(' + date.getTime() + offsetString + ')/'
		}

		/**
		 * Converts the given Date object into a .NET date string (i.e. /Date(1538398997399)/).
		 */
		return '/Date(' + date.getTime() + ')/'
	}

	private static getOffsetString(date: Date): string {
		const momentDate: moment.Moment = moment(date)
		const offset: number = momentDate.utcOffset() / 60
		const sign: string = offset < 0 ? '-' : '+'
		const prependZero: boolean = offset < 10
		const hoursPart: number = Math.floor(offset)
		const minutesPartString: string = offset & 1 ? '30' : '00'
		const hoursPartString: string = prependZero ? '0' + hoursPart : hoursPart.toString()

		return sign + hoursPartString + minutesPartString
	}

	public static isNetDateFormat(dateString: string): boolean {
		return _.isString(dateString) && dateString.substr(0, 6) === '/Date('
	}

	public static stringToDate(dateString: string, format?: string): Date {
		if (dateString == null) {
			throw new Error('The date string cannot be undefined.')
		}

		let date: Date = null

		if (format == null || format == '') {
			format = ConvertHelper.DefaultDateTimeFormat
		}

		if (ConvertHelper.isNetDateFormat(dateString)) {
			/**
			 * Converts the given .NET date string (i.e. /Date(1538398997399)/) into a Date object.
			 *
			 * Miliseconds in the incomming .NET date describes the time that is local to the client's machine.
			 * Timezone in the incomming .NET date is the timezone of the current server, so the JavaScript date
			 * is created using the YYYY-MM-DD HH:mm:ss.SSS format, so the timezone information from the .NET
			 * date is ignored.
			 */
			date = moment(moment.parseZone(dateString).format(format), format).toDate()
		} else {
			date = moment(dateString, format).toDate()
		}

		if (date == null || isNaN(date.getTime())) {
			console.warn(ConvertHelper.LOG_STRING, 'Unable to parse the given date.', dateString)
		}

		return date
	}

	// #endregion

	// #region Dynamic Values

	public static dynamicValueToIOpData(dataProvider: DataProvider, dynamicValue: { Key: string; Value: any }): JQueryPromise<IOpData> {
		if (dataProvider != null && dynamicValue != null) {
			const dynamicValueParts: string[] = dynamicValue.Key.split('_')
			const idDataType: number = Number(dynamicValueParts[1])
			const index: number = Number(dynamicValueParts[2])

			if (_.isFinite(idDataType) && _.isFinite(index)) {
				const data: OpData = new OpData(null)

				data.idDataType = idDataType
				data.indexNbr = index
				data.value = dynamicValue.Value

				return dataProvider.DataType.getDataType(data.idDataType).then((dataType: OpDataType) => {
					data.dataType = dataType

					return data
				})
			}
		}

		return $.Deferred<IOpData>().resolve(null).promise()
	}

	public static dynamicValuesToIOpData(dataProvider: DataProvider, dynamicValues: { Key: string; Value: any }[]): JQueryPromise<IOpData[]> {
		const dataPromises: JQueryPromise<IOpData>[] = _.map(dynamicValues, (dynamicValue) => ConvertHelper.dynamicValueToIOpData(dataProvider, dynamicValue))

		return $.when(...dataPromises).then((...objectData: IOpData[]) => objectData)
	}

	// #endregion

	// #region Enums

	/**
	 * Returns a string value for the given enum and its value.
	 */
	public static enumToString(enumObject: any, enumValue: any): string {
		if (enumObject == null) {
			return null
		}

		return enumObject[enumValue]
	}

	/**
	 * Parses any value to enum. If parse fails, null is returned.
	 * Example usages:
	 *      parseEnum(ReferenceType, 8) // returns 8
	 *      parseEnum(ReferenceType, ReferenceType.IdLocation) // returns 8
	 *      parseEnum(ReferenceType, 'IdLocation') // returns 8
	 *      parseEnum(ReferenceType, '8') // returns 8
	 *      parseEnum(ReferenceType, 'asdadssad') // returns null
	 * @param enumType Type of enum. For example: ReferenceType
	 * @param value Value to parse
	 */
	public static parseEnum<T>(enumType: any, value: number | string): T {
		let retVal: any = null

		if (value !== null && value !== undefined && enumType !== null && enumType !== undefined) {
			if (typeof value === 'string') {
				let numValue: number = +value

				if (isNaN(numValue)) {
					retVal = enumType[value]
				} else {
					value = numValue
				}
			}

			if (typeof value === 'number' || typeof value === typeof enumType) {
				retVal = value
			}

			if (!isFinite(retVal)) {
				retVal = null
			}
		}

		if (retVal === undefined) {
			retVal = null
		}

		return retVal === null ? null : <T>retVal
	}

	// #endregion

	// #region Number

	public static localizedStringToNumber(value: string): number {
		if (value != null) {
			return globalizeParseNumber(value)
		}

		return null
	}

	public static numberToLocalizedString(value: number): string {
		if (value != null) {
			return globalizeFormatNumber(value)
		}

		return null
	}

	//#endregion

	// #region File

	public static getBase64EncodedFileExtension(stringToEncode: string): string {
		if (_.size(stringToEncode) === 1) {
			if (stringToEncode === 'i') {
				return ConvertHelper.enumToString(Enums.FileExtension, Enums.FileExtension.Png).toLowerCase()
			}
			if (stringToEncode === '/') {
				return ConvertHelper.enumToString(Enums.FileExtension, Enums.FileExtension.Jpg).toLowerCase()
			}
			if (stringToEncode === 'R') {
				return 'gif'
			}
		}
		return '*'
	}

	public static fileToDataUrlImage(imageFile: OpFile): string {
		let base64Image: string
		if (imageFile != null && imageFile.fileBytes != null) {
			if (ConvertHelper.isImage(imageFile.extension)) {
				const fileBytes = imageFile.fileBytes

				if (_.isArray(fileBytes)) {
					base64Image = ConvertHelper.byteArrayToBase64(fileBytes)
				} else {
					base64Image = fileBytes
				}
			} else {
				LogHelper.error(this.LOG_STRING, `Incorrect file extension + ${imageFile.extension}`)
				return null
			}

			const extension: string = ConvertHelper.enumToString(Enums.FileExtension, imageFile.extension)
			if (extension != null) {
				return `data:image/${extension.toLowerCase()};base64,${base64Image}`
			}

			return `data:image/png;base64,` + base64Image
		}
		LogHelper.error(this.LOG_STRING, `Incorrect file + ${imageFile}`)

		return null
	}

	private static isImage(extension: FileExtension): boolean {
		if (extension != null) {
			return extension === Enums.FileExtension.Png || extension === Enums.FileExtension.Jpg || extension === Enums.FileExtension.Jpeg || extension === Enums.FileExtension.Bmp
		}
		return false
	}

	// #endregion

	// #region Hash

	public static hash(object: any): string {
		return Md5.init(object)
	}

	//#endregion

	// #region Path

	public static getOriginFromUrl(url: string): string {
		if (url != null) {
			const urlArray: string[] = url.split('/')
			const protocol: string = urlArray[0]
			const host: string = urlArray[2]

			return protocol + '//' + host
		}

		return null
	}

	// #endregion

	// #region SIMAX Errors

	public static getRequestErrorMessage(error: any): string {
		if (error == null) {
			return null
		}

		if (SESSION.isType(error)) {
			return (<SESSION>error).toString()
		}

		if (_.isFunction(error.toString)) {
			return error.toString()
		}

		return error
	}

	// #endregion

	// #region String

	public static stringToSnakeCase(str: string, separator: string = '_') {
		return str.replace(ConvertHelper.SNAKE_CASE_REGEXP, (letter, pos) => {
			return (pos ? separator : '') + letter.toLowerCase()
		})
	}

	// #endregion

	// #region String Encoding

	/**
	 * Converts the given base64 encoded string into an array of bytes.
	 */
	public static base64ToByteArray(base64String: string): number[] {
		if (!base64String) {
			return []
		}

		const byteString: string = atob(base64String)
		const bytes: number[] = []

		for (let idx: number = 0; idx < byteString.length; idx++) {
			bytes[idx] = byteString.charCodeAt(idx)
		}

		return bytes
	}

	/**
	 * Convets the given array of bytes into a base64 encoded string.
	 * Uses the btoa function so it supports only ASCII charaters.
	 */
	public static byteArrayToBase64(byteArray: number[]): string {
		if (!byteArray) {
			return ''
		}

		const byteString: string = byteArray.map((byte: number) => String.fromCharCode(byte)).join('')

		return btoa(byteString)
	}

	/**
	 * Converts the given byte array into a Unicode string.
	 */
	public static byteArrayToUnicodeString(byteArray: number[]): string {
		if (byteArray == null) {
			throw new Error('The byte array cannot be undefined.')
		}

		const byteString: string = byteArray.map((byte: number) => String.fromCharCode(byte)).join('')

		const encodedString: string = byteString.replace(/(.)/g, (match: string, p1: string) => {
			let code = p1.charCodeAt(0).toString(16).toUpperCase()

			if (code.length < 2) {
				code = '0' + code
			}

			return '%' + code
		})

		return decodeURIComponent(encodedString)
	}

	/**
	 * Converts the given Unicode string into an array of bytes (code units).
	 */
	public static unicodeStringToByteArray(unicodeString: string): number[] {
		if (unicodeString == null) {
			throw new Error('The Unicode string cannot be undefined.')
		}

		const encodedString: string = encodeURIComponent(unicodeString)
		const byteString: string = encodedString.replace(/%([0-9A-F]{2})/g, (match: string, p1: string) => {
			return String.fromCharCode(+('0x' + p1))
		})

		const bytes: number[] = []

		for (let index = 0; index < byteString.length; index++) {
			bytes[index] = byteString[index].charCodeAt(0)
		}

		return bytes
	}

	// #endregion
}
